import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { type TFunction } from 'i18next';
import DownloadIcon from '@/assets/icons/download-icon.svg?react';
import FileIcon from '@/assets/icons/file-icon.svg?react';
import { MessageStatus, type UploadedFile } from '@/types/message.types';
import { formatFileSize } from '@/utils/file';
import type { DocMessageProps } from './types';

const formatFilename = ({ file, t }: { file: UploadedFile; t: TFunction }) =>
  file.filename?.split('/').pop() || t('messages.unknownFile');

/**
 * DocMessage - Renders doc attachment messages as individual cards
 */
export const DocMessage = React.memo<DocMessageProps>(({ message }) => {
  const { t } = useTranslation('common');

  // Filter uploaded files to only non-media files
  const files = useMemo(
    () =>
      message?.uploadedFiles?.filter(
        (file: UploadedFile) =>
          !file.mimeType?.startsWith('image/') &&
          !file.mimeType?.startsWith('video/'),
      ) || [],
    [message?.uploadedFiles],
  );

  const handleDownload = useCallback(async (file: UploadedFile) => {
    const link = document.createElement('a');

    const filename = file.filename?.split('/').pop() || `media-${Date.now()}`;
    const response = await fetch(file.url);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  if (files.length === 0) {
    return null;
  }

  return (
    <>
      {files.map((file) => {
        const filename = formatFilename({ file, t });
        const fileSize = formatFileSize(file.fileSize);

        return (
          <div
            key={file.fileId}
            className={classNames({
              'sf:message-bubble sf:rounded-xl sf:p-2 sf:w-[var(--w-message-bubble)] sf:shadow-message-bubble':
                true,
              'sf:bg-red-10': message?.status === MessageStatus.FAILED,
              'sf:bg-white/60': message?.status !== MessageStatus.FAILED,
            })}
          >
            <div className="sf:bg-white sf:rounded-lg sf:p-2 sf:w-full sf:h-full sf:shadow-message-bubble sf:flex sf:flex-row sf:items-center sf:gap-4">
              <div className="sf:flex-shrink-0 sf:bg-bg-disabled sf:w-16 sf:h-16 sf:rounded-lg sf:flex sf:items-center sf:justify-center">
                <FileIcon />
              </div>

              <div className="sf:flex sf:flex-col sf:justify-between">
                <p className="sf:text-sm sf:font-semibold sf:text-gray-900 sf:truncate sf:max-w-[216px]">
                  {filename}
                </p>

                <div className="sf:flex sf:items-center sf:text-xs sf:text-gray-90 sf:mt-1 sf:gap-1">
                  {fileSize}
                </div>

                <button
                  onClick={() => handleDownload(file)}
                  className="sf:flex sf:items-center sf:text-xs sf:mt-3 sf:font-semibold sf:text-widget-button-color sf:rounded sf:cursor-pointer"
                  title={t('messages.download')}
                  aria-label={t('messages.download')}
                >
                  <DownloadIcon className="sf:w-4 sf:h-4" />
                  <span className="sf:ml-1">{t('messages.download')}</span>
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
});

DocMessage.displayName = 'DocMessage';
